import styled from '@emotion/styled';
import { Div, Heading, Label, P } from '@kizi/core-components';
import { Radio, RadioProps } from '@kizi/shared-components';
import React from 'react';
import { MainCoveragePrice, MainCoveragePriceProps } from './Price';
import { MainCoverageProsCons, MainCoverageProsConsProps } from './ProsCons';

const InputWrap = styled(Div)`
  grid-area: input;
`;

const Advise = styled(P)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
`;

const SubTitle = styled(P)`
  grid-area: subTitle;
`;

export interface MainCoverageBlockProps {
  title: string;
  subTitle: string;
  prosCons: MainCoverageProsConsProps;
  price?: MainCoveragePriceProps;
  radio?: RadioProps;
  advise?: {
    bg?: string;
    color?: string;
    text: string;
  };
}

function getGridProps(
  radio: MainCoverageBlockProps['radio'],
  price: MainCoverageBlockProps['price']
) {
  const radioDesktopArea = radio
    ? `"separator"
       "input"`
    : '';
  return {
    gridRowGap: [1, null, 3],
    gridColumnGap: 3,
    gridTemplateAreas: [
      `
        "${radio ? 'input' : ''} title ${price ? 'price' : ''}"
        "${radio ? 'input' : ''} subTitle ${price ? 'price' : ''}"
        "${radio ? 'input' : ''} prosCons ${price ? 'price' : ''}"
        `,
      null,
      `
        "title"
        "subTitle"
        "prosCons"
        ${price ? '"price"' : ''}
        ${radioDesktopArea}
        `,
    ],
    gridTemplateColumns: [
      `${radio ? 'max-content' : ''} auto ${price ? 'max-content' : ''}`,
      null,
      'auto',
    ],
  };
}

export const MainCoverageBlock: React.VFC<MainCoverageBlockProps> = ({
  title,
  subTitle,
  prosCons,
  price,
  radio,
  advise,
}) => {
  const handleChange = React.useCallback(() => {
    radio?.onChange?.({
      target: {
        name: radio.name as string,
        value: radio.value as string,
        checked: !radio.checked,
      },
    } as React.ChangeEvent<any>);
  }, [radio]);
  const handleClick = React.useCallback<
    React.MouseEventHandler<HTMLDivElement>
  >(
    (event) => {
      if (event.target instanceof HTMLInputElement) {
        return;
      }
      handleChange();
    },
    [handleChange]
  );

  return (
    <Div
      position="relative"
      display="grid"
      alignContent="baseline"
      {...getGridProps(radio, price)}
      variant="primaryCard"
      mt={[advise ? 2 : 0, null, 0]}
      padding={[3, null, 6]}
      borderWidth={2}
      borderStyle="solid"
      borderColor={radio?.checked ? 'main.1' : 'transparent'}
      style={{
        boxSizing: 'border-box',
        cursor: radio ? 'pointer' : '',
      }}
      onClick={handleClick}
      data-testid="MainCoverageBlock"
    >
      {advise && (
        <Advise
          borderRadius={20}
          variant="captionImportant"
          bg={advise.bg || 'supplementary.1'}
          color={advise.color || 'dark.1'}
          py={1}
          px={2}
          data-testid="MainCoverageBlock.advise"
        >
          {advise.text}
        </Advise>
      )}
      <Heading
        gridArea="title"
        as="h4"
        variant="H4"
        textAlign={[null, null, 'center']}
        my={0}
        data-testid="MainCoverageBlock.title"
      >
        {title}
      </Heading>
      <SubTitle
        variant="captionRegular"
        textAlign={[null, null, 'center']}
        data-testid="MainCoverageBlock.subTitle"
      >
        {subTitle}
      </SubTitle>
      <MainCoverageProsCons
        {...prosCons}
        gridArea="prosCons"
        data-testid="MainCoverageBlock.prosCons"
      />
      {price && (
        <MainCoveragePrice
          {...price}
          gridArea="price"
          alignSelf="flex-start"
          data-testid="MainCoverageBlock.price"
        />
      )}
      {radio && (
        <>
          <Div
            borderBottom="1px solid"
            borderColor="divider"
            display={['none', null, 'block']}
            gridArea="separator"
            data-testid="MainCoverageBlock.divider"
          />
          <Radio
            id={radio.name}
            variant="main"
            {...radio}
            mx={0}
            width={20}
            height={20}
            data-testid="MainCoverageBlock.radio"
            containerProps={{
              ...radio.containerProps,
              as: InputWrap,
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row-reverse',
              justifyContent: ['center', null, 'start'],
            }}
            label={
              <Label
                variant="paragraphs.bodyRegular"
                ml={2}
                display={['none', null, 'block']}
                style={{
                  cursor: 'pointer',
                }}
                data-testid="MainCoverageBlock.radio.label"
              >
                {radio.label}
              </Label>
            }
            onChange={handleChange}
          />
        </>
      )}
    </Div>
  );
};
